/** ===============================
 *  FORTUNE GAME Handler MODULE
 *
 *  Injects a fortune prize section into the GWP drawer that custom.js manages.
 *  GWP product selection is owned by custom.js via window.tatcha.gwpTierPromos.
 *  This module appends the prize reveal card below the GWP grid items and
 *  hooks the confirm button to also add the prize when the user confirms.
 * =============================== */
class FortuneGameHandler {

  static init() {
    this.drawer = document.querySelector('#gwp-cart-drawer');
    if (!this.drawer) {
      console.warn('Fortune Game: GWP drawer not found');
      return;
    }

    this._fortuneActive = false;
    this._currentTier = null;
    this._currentEntry = null;
    this._revealed = false;
    this._wonPrizeVariant = null;
    this._fortuneMessage = null;
    this._prizeAccepted = false;
    this._gameCompleted = false;
    this._confirmInFlight = false;
    this._evalAbortToken = 0;
    this._injectTimer = null;
    this._productTagsCache = {};
    this._evaluating = false;
    this._pendingEvaluate = false;

    // Re-inject prize section whenever custom.js rebuilds the grid.
    // Debounced 50ms so we wait for all product nodes to be appended.
    const grid = document.getElementById('gwp-grid');
    if (grid) {
      this._observer = new MutationObserver(() => {
        if (!this._fortuneActive) return;
        clearTimeout(this._injectTimer);
        this._injectTimer = setTimeout(() => {
          if (!document.getElementById('fortune-prize-section')) {
            this._injectPrizeSectionIfReady();
          }
        }, 50);
      });
      this._observer.observe(grid, { childList: true });
    }

    document.addEventListener('cart:refresh', () => this.evaluateFortuneGame());
    // cart:change fires when the user adds/removes products via the cart UI.
    // The theme does not dispatch cart:refresh in that path, so we need both.
    document.addEventListener('cart:change', () => this.evaluateFortuneGame());
    // Immediately reset all state when the discount code is removed so the
    // scratch card shows fresh on re-application, regardless of event timing.
    document.addEventListener('fortune:reset', () => {
      this._fortuneActive = false;
      this._revealed = false;
      this._wonPrizeVariant = null;
      this._fortuneMessage = null;
      this._gameCompleted = false;
      this._confirmInFlight = false;
      this._evalAbortToken++;
      document.getElementById('fortune-prize-section')?.remove();
    });

    // React after custom.js has finished adding/removing GWP items on confirm.
    // Using an event rather than a second button listener avoids the button-recreation
    // problem and eliminates the race between the two confirm handlers.
    // custom.js passes the latest cart in event.detail.cart so we can skip an
    // extra /cart.js round-trip. Fall back to fetching when not provided.
    document.addEventListener('gwp:confirmed', async (e) => {
      const passedCart = e.detail?.cart ?? null;
      const prizeAddedInBatch = e.detail?.prizeAddedInBatch ?? false;
      const prizeInCartFromCustom = e.detail?.prizeInCart ?? null; // null = unknown, true/false = confirmed

      // Game already completed but user unchecked the prize — remove it from cart.
      // Don't set tier_done: prize stays re-addable.
      if (this._gameCompleted && this._wonPrizeVariant && !this._prizeAccepted) {
        this._confirmInFlight = true;
        try {
          // Use passed cart to avoid an extra /cart.js fetch. custom.js already
          // verified the prize is gone before dispatching, so prizeLine will be
          // null in the common path and we skip the change.js call entirely.
          const freshCart = passedCart || await fetch('/cart.js').then(r => r.json());
          const prizeLine = freshCart.items.find(i => i.properties?._fortune_type === 'prize');
          if (prizeLine) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: prizeLine.key, quantity: 0 }),
            });
          }
        } catch (e) {
          console.error('[FortuneGame] Error removing prize:', e);
        } finally {
          this._confirmInFlight = false;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
        }
        return;
      }

      // Game completed + prize was removed + user re-checked it — add it back.
      // Guard: use passed cart to confirm the prize is actually missing before
      // adding, so a tier-change evaluate that set _prizeAccepted=true on an
      // already-in-cart prize doesn't create a duplicate line item.
      if (this._gameCompleted && this._wonPrizeVariant && this._prizeAccepted) {
        const tier = this._currentTier;
        this._confirmInFlight = true;
        try {
          // prizeInCartFromCustom = true means custom.js confirmed via syncWithCart that the
          // prize is already in the cart. Skip the fresh /cart.js fetch — saves ~1.5s.
          let prizeAlreadyInCart;
          let cartForOps = null; // only needed if prize is missing and _makePrizeRoom must run
          if (prizeInCartFromCustom === true) {
            prizeAlreadyInCart = true;
          } else {
            cartForOps = passedCart || await fetch('/cart.js').then(r => r.json());
            prizeAlreadyInCart = cartForOps?.items?.some(i => i.properties?._fortune_type === 'prize') ?? false;
          }
          if (!prizeAlreadyInCart) {
            // Verify the qualifying tier hasn't changed since the game was completed.
            // Guards against a race where _currentTier is stale because evaluateFortuneGame
            // is still in-flight processing a tier downgrade (e.g. user removed a product).
            const freshEntry = this.getFortuneGameEntry();
            if (freshEntry) {
              const freshSubtotal = await this.calculateEligibleSubtotal(cartForOps, freshEntry);
              const freshTier = this.getQualifyingTier(freshSubtotal, freshEntry);
              if (!freshTier || freshTier.name !== tier.name) {
                // Tier changed — don't re-add the prize. Fire confirm to unblock UI
                // and trigger a fresh evaluate to re-settle game state.
                document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
                this._confirmInFlight = false;
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
                return;
              }
            }
            // On re-add path (game already completed), never silently evict a confirmed
            // GWP. If the tier is full, skip the re-add — the user must free a slot first.
            const gwpLines = (cartForOps.items || []).filter(
              i => i.properties?._fortune_game === 'true' && i.properties?._fortune_type !== 'prize'
            );
            const maxGwp = tier?.max_gwp ?? 0;
            if (maxGwp && gwpLines.length >= maxGwp) {
              this._prizeAccepted = false;
              document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
              this._confirmInFlight = false;
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              return;
            }
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: this._wonPrizeVariant.id,
                quantity: 1,
                properties: {
                  _gwp: 'true',
                  _fortune_game: 'true',
                  _fortune_tier: tier.name,
                  _fortune_type: 'prize',
                },
              }),
            });
          }
          document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
          // Attribute write in background — keep _confirmInFlight true until done.
          this._writeCompletionAttrs(tier, false)
            .catch(e => console.error('[FortuneGame] _writeCompletionAttrs (bg):', e))
            .finally(() => {
              this._confirmInFlight = false;
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            });
        } catch (e) {
          console.error('[FortuneGame] Error re-adding prize:', e);
          this._confirmInFlight = false;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
        }
        return;
      }

      if (!this._fortuneActive || this._gameCompleted || !this._revealed) {
        // Nothing to do — signal immediately so any waiting UI can close.
        document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
        return;
      }
      const tier = this._currentTier;
      this._confirmInFlight = true;
      const prizeAccepted = this._wonPrizeVariant && this._prizeAccepted;

      if (prizeAccepted) {
        // Path 3a: prize accepted.
        // If custom.js already batched the prize into the GWP add.js call, skip the
        // add here and go straight to the background attribute write.
        // If not batched (e.g. no GWPs were added, or maxGwp was reached), add prize
        // now with _makePrizeRoom check, then background the attribute write.
        // Keep _confirmInFlight = true while _writeCompletionAttrs runs so
        // evaluateFortuneGame doesn't re-fire before _fortune_game_complete is written.
        try {
          if (!prizeAddedInBatch) {
            const cartForPrize = passedCart || await fetch('/cart.js').then(r => r.json());
            await this._makePrizeRoom(cartForPrize, tier);
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: this._wonPrizeVariant.id,
                quantity: 1,
                properties: {
                  _gwp: 'true',
                  _fortune_game: 'true',
                  _fortune_tier: tier.name,
                  _fortune_type: 'prize',
                },
              }),
            });
          }
          this._fortuneActive = false;
          // Prize is in cart — drawer can close now.
          document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
          // Attribute write runs after drawer closes. _confirmInFlight stays true until done.
          this._writeCompletionAttrs(tier, false)
            .catch(e => console.error('[FortuneGame] _writeCompletionAttrs (bg):', e))
            .finally(() => {
              this._confirmInFlight = false;
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            });
        } catch (e) {
          console.error('[FortuneGame] Error adding fortune prize on confirm:', e);
          this._confirmInFlight = false;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
        }
        return;
      }

      // Path 3b: no prize won, or prize won but not accepted — write completion attr.
      try {
        if (!this._wonPrizeVariant) {
          await this._writeCompletionAttrs(tier, true, this._fortuneMessage);
        } else {
          // Prize won but not accepted — persist state so the checkout extension
          // can restore the prize card even though it isn't in the cart yet.
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                _fortune_game_complete: tier.name,
                _fortune_prize_id: `gid://shopify/ProductVariant/${this._wonPrizeVariant.id}`,
              },
            }),
            signal: AbortSignal.timeout(8000),
          });
        }
        this._fortuneActive = false;
      } catch (e) {
        console.error('[FortuneGame] Error in fortune prize step:', e);
      } finally {
        this._confirmInFlight = false;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.documentElement.dispatchEvent(new CustomEvent('fortune:confirmed', { bubbles: true }));
      }
    });

    this.evaluateFortuneGame();
  }

  static getFortuneGameEntry() {
    const promos = window.tatcha?.gwpTierPromos;
    if (!promos) return null;
    const entry = Object.values(promos).find(p => p.type === 'fortune_game') || null;

    // SHOP-1058: when restriction is enabled, only show the fortune game to the
    // DY-targeted early-access audience (cookie set by DY campaign).
    if (entry && window.restrictFukubikiPromo && !document.cookie.includes('dy_fukubiki_ea=1')) {
      return null;
    }
    return entry;
  }

  // State machine for the fortune game. Called on every cart:refresh.
  // _fortune_game_complete cart attribute drives restore logic across page loads:
  //   ""            → game not yet played for this code application
  //   "tierN"       → game played at tierN, prize was won (prize line should be in cart)
  //   "tierN_done"  → game played at tierN, no prize was won
  // A completion attribute from a *different* tier than the current qualifying tier means
  // the cart subtotal changed after the game was played (tier upgrade/downgrade).
  static async evaluateFortuneGame() {
    if (this._confirmInFlight) { return; }
    if (this._evaluating) {
      this._pendingEvaluate = true;
      return;
    }
    this._evaluating = true;
    const evalId = this._evalAbortToken;
    try {
      const entry = this.getFortuneGameEntry();
      if (!entry || !entry.enabled) {
        this._fortuneActive = false;
        // SHOP-1058: the promo may be gated for this shopper (restricted audience),
        // in which case getFortuneGameEntry() returns null. We must still purge any
        // stale fortune line items / completion attribute — otherwise they leak into
        // checkout with no backing discount and send it into a recalc/re-render loop
        // (observed crashing Safari). Skipped once the cart is confirmed clean, and
        // self-limiting: removeFortuneGameItems only re-dispatches cart:refresh when
        // it actually removed something.
        if (!this._gatedCartClean) {
          try {
            const gatedCart = await fetch('/cart.js').then(r => r.json());
            if (this._evalAbortToken !== evalId) return;
            const hasStaleFortuneState =
              gatedCart.items?.some(item => item.properties?._fortune_game === 'true') ||
              !!gatedCart.attributes?._fortune_game_complete;
            if (hasStaleFortuneState) {
              await this.removeFortuneGameItems(gatedCart);
            } else {
              this._gatedCartClean = true;
            }
          } catch (e) {
            console.error('[FortuneGame] Error cleaning up while promo gated:', e);
          }
        }
        return;
      }
      // Promo is available again — re-arm gated cleanup in case it re-gates later
      // (e.g. the dy_fukubiki_ea cookie expires mid-session).
      this._gatedCartClean = false;

      const cart = await fetch('/cart.js').then(r => r.json());
      if (this._evalAbortToken !== evalId) return;

      const promoCode = String(entry.promoCode || '');
      const codeApplied = cart.discount_codes?.some(
        dc => dc.code?.toLowerCase() === promoCode.toLowerCase() && dc.applicable === true
      );
      if (!codeApplied) {
        this._fortuneActive = false;
        await this.removeFortuneGameItems(cart);
        if (this._evalAbortToken !== evalId) return;
        return;
      }

      const eligibleSubtotal = await this.calculateEligibleSubtotal(cart, entry);
      if (this._evalAbortToken !== evalId) return;
      const tier = this.getQualifyingTier(eligibleSubtotal, entry);
      if (!tier) {
        // Cart subtotal dropped below all tier thresholds — clean up and reset.
        // Do NOT remove the discount code so the user doesn't have to re-enter it.
        await this.removeFortuneGameItems(cart);
        if (this._evalAbortToken !== evalId) return;
        if (cart.attributes?._fortune_game_complete) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _fortune_game_complete: '' } }),
          });
          if (this._evalAbortToken !== evalId) return;
        }
        this._fortuneActive = false;
        this._revealed = false;
        this._wonPrizeVariant = null;
        this._fortuneMessage = null;
        this._gameCompleted = false;
        document.getElementById('fortune-prize-section')?.remove();
        return;
      }

      // Restore state from the cart attribute so the correct UI shows after
      // a page reload or a cart:refresh triggered by an external change.
      const completionAttr = cart.attributes?._fortune_game_complete;
      if (completionAttr) {
        // ── No-prize path: "tierN_done" matches the current tier ─────────────
        // The scratch card was already revealed and no prize was awarded.
        // Show the fortune-message banner; the game is done for this tier.
        if (completionAttr === tier.name + '_done') {
          document.getElementById('fortune-prize-section')?.remove();
          this._fortuneActive = true;
          this._gameCompleted = true;
          this._currentTier = tier;
          this._currentEntry = entry;
          this._revealed = true;
          this._wonPrizeVariant = null;
          this._injectPrizeSectionIfReady();
          return;
        }

        // ── Prize path: "tierN" — game completed, prize may or may not be in cart ──
        const prizeItem = cart.items.find(item => item.properties?._fortune_type === 'prize');

        if (completionAttr === tier.name) {
          if (prizeItem) {
            // Normal completed state: prize won and still in cart.
            const wonPrize = (entry.fortunePrizeList || []).find(v => v.id == prizeItem.variant_id)
              || this._wonPrizeVariant;
            this._fortuneActive = true;
            this._gameCompleted = true;
            this._currentTier = tier;
            this._currentEntry = entry;
            this._revealed = true;
            this._wonPrizeVariant = wonPrize || null;
            this._prizeAccepted = true;
            this._injectPrizeSectionIfReady();
            return;
          }
          // Prize line removed — user unchecked it. If _wonPrizeVariant is still in memory,
          // keep _fortune_game_complete set so the checkout extension can restore the
          // prize card. User can re-add from the cart drawer or from checkout.
          if (this._wonPrizeVariant) {
            this._fortuneActive = true;
            this._gameCompleted = true;
            this._currentTier = tier;
            this._currentEntry = entry;
            this._revealed = true;
            this._prizeAccepted = false;
            document.getElementById('fortune-prize-section')?.remove();
            this._injectPrizeSectionIfReady();
            return;
          }
          // No prize in memory (e.g. page was refreshed) — clear stale attributes
          // so the game resets cleanly.
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _fortune_game_complete: '', _fortune_prize_id: '' } }),
          });
          if (this._evalAbortToken !== evalId) return;
        } else {
          // ── Game completed at a *different* tier (subtotal changed after reveal) ──
          // Always keep _gameCompleted = true — the user has already played;
          // never re-offer the scratch card just because the tier changed.
          let wonPrize = null;
          if (!completionAttr.endsWith('_done')) {
            // Prize path — restore variant from cart, memory, or stored GID.
            if (prizeItem) {
              wonPrize = (entry.fortunePrizeList || []).find(v => v.id == prizeItem.variant_id) || this._wonPrizeVariant;
            } else {
              const storedGid = !this._wonPrizeVariant ? cart.attributes?._fortune_prize_id : null;
              const restoredPrize = storedGid
                ? (entry.fortunePrizeList || []).find(v => String(v.id) === storedGid.split('/').pop()) || null
                : null;
              wonPrize = this._wonPrizeVariant || restoredPrize;
            }
          } else if (!this._fortuneMessage) {
            // Fortune message path — restore text from cart attribute if not in memory.
            this._fortuneMessage = cart.attributes?._fortune_message || null;
          }
          this._fortuneActive = true;
          this._gameCompleted = true;
          this._currentTier = tier;
          this._currentEntry = entry;
          this._revealed = true;
          this._wonPrizeVariant = wonPrize;
          this._prizeAccepted = !!prizeItem;
          this._injectPrizeSectionIfReady();
          return;
        }
      }

      // ── No completion attribute (or fell through above) ───────────────────
      // Game is active and not yet played for this tier. If _gameCompleted was
      // true from a previous tier, reset revealed state so the scratch card shows
      // fresh. This also fires when the promo code is removed and re-applied, since
      // removeFortuneGameItems clears the attribute but a cart:refresh may arrive
      // before evaluateFortuneGame has a chance to clean up in-memory flags.
      const wasCompleted = this._gameCompleted;
      this._fortuneActive = true;
      this._gameCompleted = false;
      this._currentTier = tier;
      this._currentEntry = entry;

      if (wasCompleted) {
        this._revealed = false;
        this._wonPrizeVariant = null;
        this._fortuneMessage = null;
        document.getElementById('fortune-prize-section')?.remove();
      }

      this._injectPrizeSectionIfReady();

    } catch (e) {
      console.error('Fortune Game evaluation error:', e);
    } finally {
      this._evaluating = false;
      if (this._pendingEvaluate) {
        this._pendingEvaluate = false;
        this.evaluateFortuneGame();
      }
    }
  }

  // ─── Prize section injection ──────────────────────────────────────────────

  static _bannerUrl(type) {
    const cfg = window.tatcha?.fortuneGameConfig;
    return type === 'reveal' ? (cfg?.bannerRevealUrl || '') : (cfg?.bannerResultUrl || '');
  }

  static _injectPrizeSectionIfReady() {
    if (!this._fortuneActive) { return; }
    if (document.getElementById('fortune-prize-section')) { return; }

    const grid = document.getElementById('gwp-grid');
    // Wait until custom.js has placed at least one product in the grid
    if (!grid || !grid.querySelector('[data-variant-id]')) { return; }

    const section = document.createElement('div');
    section.id = 'fortune-prize-section';

    if (this._revealed) {
      // Restore state after grid rebuild
      section.innerHTML = this._wonPrizeVariant
        ? this._buildPrizeBannerHTML(this._wonPrizeVariant)
        : this._buildNoPrizeBannerHTML(this._fortuneMessage);
      if (this._wonPrizeVariant) {
        const cb = section.querySelector('[data-fortune-prize="checkbox"]');
        if (cb) {
          cb.checked = this._prizeAccepted;
          this._attachPrizeCardListeners(section, cb);
        }
      }
    } else {
      section.innerHTML = this._buildRevealBannerHTML();
      section.querySelector('.fortune-prize-banner--reveal').addEventListener('click', () => {
        this._triggerFlipReveal(section);
      }, { once: true });
    }

    grid.prepend(section);

    if (this._revealed && this._wonPrizeVariant) {
      const cb = section.querySelector('[data-fortune-prize="checkbox"]');
      // Dispatch after prepend so the event bubbles through the grid and
      // enforceGwpLimit sees the prize checkbox in its checked state.
      cb?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  static _resolveFortunePrize(section) {
    const entry = this._currentEntry;
    const prizeList = (entry.fortunePrizeList || []).filter(v => v.stock);
    const prizeProbability = entry.prizeProbability || 0;
    const wonPrize = prizeList.length > 0 && prizeProbability > 0 && Math.random() * 100 < prizeProbability;

    if (wonPrize) {
      this._wonPrizeVariant = prizeList[Math.floor(Math.random() * prizeList.length)];
      const currentGwpCount = document.querySelectorAll('[data-gwp-cart="checkbox"]:checked').length;
      const tierFull = currentGwpCount >= (this._currentTier?.max_gwp ?? Infinity);
      this._prizeAccepted = !tierFull;
      section.innerHTML = this._buildPrizeBannerHTML(this._wonPrizeVariant);
      const cb = section.querySelector('[data-fortune-prize="checkbox"]');
      if (cb) {
        cb.checked = this._prizeAccepted;
        this._attachPrizeCardListeners(section, cb);
      }
    } else {
      this._wonPrizeVariant = null;
      this._prizeAccepted = false;
      if (!this._fortuneMessage) this._fortuneMessage = this._randomFortuneMessage();
      section.innerHTML = this._buildNoPrizeBannerHTML(this._fortuneMessage);
    }
  }

  static _attachPrizeCardListeners(section, cb) {
    cb.addEventListener('change', () => {
      this._prizeAccepted = cb.checked;
    });
    section.querySelector('.fortune-prize-banner__prize-card')?.addEventListener('click', function(e) {
      if (!e.target.closest('.product-card__select')) {
        section.querySelector('.product-card__select__label')?.click();
      }
    });
  }

  static async _triggerFlipReveal(section) {
    this._revealed = true;

    // Flip out
    section.classList.add('fortune-prize-section--flip-out');
    await new Promise(r => setTimeout(r, 260));

    // Swap content at the invisible point, then flip back in
    this._resolveFortunePrize(section);
    section.classList.remove('fortune-prize-section--flip-out');
    section.classList.add('fortune-prize-section--flip-in');

    await new Promise(r => setTimeout(r, 320));
    section.classList.remove('fortune-prize-section--flip-in');

    document.dispatchEvent(new CustomEvent('fortune:revealed', { detail: { wonPrize: !!this._wonPrizeVariant } }));

    if (this._wonPrizeVariant) {
      this._fireConfetti(section);
    } else {
      // Fortune message (no prize) — persist state immediately so the checkout
      // extension can restore it even if the user navigates without clicking Confirm.
      this._writeCompletionAttrs(this._currentTier, true, this._fortuneMessage)
        .catch(e => console.error('[FortuneGame] persist fortune message:', e));
    }
  }

  static _fireConfetti(section) {
    const banner = section.querySelector('.fortune-prize-banner--prize');
    if (!banner) return;

    banner.style.position = 'relative';
    banner.style.overflow = 'hidden';

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;border-radius:inherit;';
    banner.appendChild(canvas);

    const rect = banner.getBoundingClientRect();
    canvas.width = rect.width || 300;
    canvas.height = rect.height || 150;

    const ctx = canvas.getContext('2d');
    const colors = ['#8fba9b', '#f2e8dd', '#e8a5a5', '#c9a96e', '#ffffff', '#b5cfbd', '#e8c8b0'];

    const particles = Array.from({ length: 70 }, (_, i) => ({
      x: canvas.width * (0.25 + Math.random() * 0.5),
      y: canvas.height * 0.3,
      vx: (Math.random() - 0.5) * 11,
      vy: -(Math.random() * 8 + 2),
      size: Math.random() * 5 + 3,
      color: colors[i % colors.length],
      shape: Math.random() > 0.45 ? 'rect' : 'circle',
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.22,
      gravity: 0.17 + Math.random() * 0.1,
      drag: 0.98,
      alpha: 1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = 0;
      for (const p of particles) {
        p.vy += p.gravity;
        p.vx *= p.drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotSpeed;
        p.alpha -= 0.013;
        if (p.alpha <= 0) continue;
        alive++;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.55);
        }
        ctx.restore();
      }
      if (alive > 0) requestAnimationFrame(draw);
      else canvas.remove();
    };

    requestAnimationFrame(draw);
  }

  static _escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  static _buildRevealBannerHTML() {
    const url = this._bannerUrl('reveal');
    return `
      <div class="fortune-prize-banner fortune-prize-banner--reveal"
           style="${url ? `background-image:url('${url}')` : 'background-color:#f7e8e0'}">
        <span class="fortune-prize-banner__try-label">Try Your Luck</span>
      </div>`;
  }

  static _buildPrizeBannerHTML(variant) {
    const url = this._bannerUrl('result');
    const productPlaceholderImg = document.getElementById('gwp-product-placehodler-img')?.innerHTML || '';
    const selectorIcons = document.getElementById('gwp-selector-icons')?.innerHTML || '';
    const displayTitle = variant.product_name || variant.title || '';
    const safeTitle = this._escapeHtml(displayTitle);
    const variantSubtitle = variant.title && variant.title !== 'Default Title' && variant.title !== displayTitle
      ? `<p>${this._escapeHtml(variant.title)}</p>` : '';
    const sizeLabel = variant.size_label || variant.custom_variant || '';
    const safeId = this._escapeHtml(variant.id);

    return `
      <div class="fortune-prize-banner fortune-prize-banner--prize"
           style="${url ? `background-image:url('${url}')` : 'background-color:#e8f0e0'}">
        <p class="fortune-prize-banner__congrats">You've won a full size! Now, choose your gifts.</p>
        <div class="fortune-prize-banner__prize-card">
          <div class="line-item">
            <div class="line-item__media relative">
              ${variant.image ? `<img src="${variant.image}" width="100" height="100" alt="${safeTitle}"/>` : productPlaceholderImg}
              <div class="product-card__select">
                <input class="product-card__select__input" data-fortune-prize="checkbox"
                       type="checkbox" name="prize" value="${safeId}"
                       id="fortune-prize-${safeId}" checked>
                <label class="product-card__select__label" for="fortune-prize-${safeId}">
                  ${selectorIcons}
                </label>
              </div>
            </div>
            <div class="small-body-text v-stack gap-0.5">
              <p class="bold">${safeTitle}</p>
              ${variantSubtitle}
              <p>${sizeLabel ? `${this._escapeHtml(sizeLabel)} | ` : ''}$${(variant.price / 100).toFixed(2)} Value</p>
            </div>
          </div>
        </div>
      </div>`;
  }

  static _buildNoPrizeBannerHTML(message = '') {
    if (!message) message = this._fortuneMessage || this._randomFortuneMessage();
    const url = this._bannerUrl('result');
    return `
      <div class="fortune-prize-banner fortune-prize-banner--no-prize"
           style="${url ? `background-image:url('${url}')` : 'background-color:#e8f0e0'}">
        <p class="fortune-prize-banner__no-prize-title">Luck has brought you a special fortune:</p>
        <p class="fortune-prize-banner__quote">✦ &ldquo;${this._escapeHtml(message)}&rdquo; ✦</p>
      </div>`;
  }

  static _randomFortuneMessage() {
    const messages = [
      ' You will continue to see positive highs in your life; make sure to remember those feelings!',
      `On your upcoming trip, you will have the most thrilling time. Don't forget to take photos!`,
      `Someone close to you has something to say; let them know you are all ears!`,
      `Something new will shine in your career. Make sure to always trust your instincts!`,
      `You will see improvements in your well-being; continue to be kind to yourself!`,
      `Something troubling you will soon disappear. Keep good thoughts in your mind!`
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  // TODO: this doesn't handle entry.requiredCyos (should return 0 and count only CYOS
  // items when set) or entry.included_or_excluded_list / entry.select_products_eligibility.
  // If either is ever configured on a fortune_game promo, the qualifying tier here will
  // diverge from custom.js getSubtotal and the GWP drawer. Align with getSubtotal if needed.
  static async calculateEligibleSubtotal(cart, entry) {
    let resolvedTags = {};
    if (entry.excludedProductTag) {
      cart.items
        .filter(item => this._productTagsCache[item.product_id] === undefined)
        .forEach(item => {
          this._productTagsCache[item.product_id] = fetch(`/products/${item.handle}.js`)
            .then(r => r.json())
            .then(p => p.tags || [])
            .catch(() => []);
        });
      resolvedTags = Object.fromEntries(
        await Promise.all(
          cart.items.map(async item => [item.product_id, await this._productTagsCache[item.product_id]])
        )
      );
    }
    let subtotal = 0;
    cart.items.forEach(item => {
      if (!item.requires_shipping) return;
      if (item.properties?._gwp === 'true' || item.properties?._gwp_tier === 'true' || item.properties?._gwp_selector === 'true' || item.properties?._is_sample === 'true') return;
      if (entry.excludedProductTag && resolvedTags[item.product_id]?.includes(entry.excludedProductTag)) return;
      if (item.properties?._is_byos_gift === 'true') return;
      if (!entry.byos && item.properties?._is_byos === 'true') return;
      if (!entry.cyos && (item.properties?._is_cyos === 'true' || item.properties?._is_cyos_gift === 'true')) return;
      if (!entry.og && item.properties?._is_og_product) return;
      subtotal += item.final_line_price;
    });
    return subtotal;
  }

  static getQualifyingTier(subtotalCents, entry) {
    const dollars = subtotalCents / 100;
    const tiers = [
      { name: 'tier3', threshold: entry.tier3_threshold, max_gwp: entry.tier3_max_gwp, gwplist: entry.tier3_gwplist },
      { name: 'tier2', threshold: entry.tier2_threshold, max_gwp: entry.tier2_max_gwp, gwplist: entry.tier2_gwplist },
      { name: 'tier1', threshold: entry.tier1_threshold, max_gwp: entry.tier1_max_gwp, gwplist: entry.tier1_gwplist },
    ];
    for (const tier of tiers) {
      if (tier.threshold > 0 && dollars >= tier.threshold) return tier;
    }
    return null;
  }

  // If the cart already has max_gwp GWP lines, remove the last one so the prize
  // can be added without exceeding the tier limit. Handles the edge case where
  // the user confirms with a full GWP selection and a checked (but not yet added) prize.
  static async _makePrizeRoom(cart, tier) {
    const maxGwp = tier?.max_gwp ?? 0;
    const gwpLines = (cart.items || []).filter(
      i => i.properties?._fortune_game === 'true' && i.properties?._fortune_type !== 'prize'
    );
    if (!maxGwp || gwpLines.length < maxGwp) return;
    const last = gwpLines[gwpLines.length - 1];
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: last.key, quantity: 0 }),
    });
  }

  // Returns prize info that custom.js can batch into a single /cart/add.js call.
  // Returns null when the prize should not be added (game not active, already done, etc.).
  static getPendingPrize() {
    if (
      this._fortuneActive &&
      !this._gameCompleted &&
      this._revealed &&
      this._wonPrizeVariant &&
      this._prizeAccepted
    ) {
      return { wonPrize: this._wonPrizeVariant, currentTier: this._currentTier };
    }
    return null;
  }

  static async _writeCompletionAttrs(tier, noPrize = false, fortuneMessage = null) {
    try {
      const value = noPrize ? tier.name + '_done' : tier.name;
      const attributes = { _fortune_game_complete: value };
      if (noPrize && fortuneMessage) attributes._fortune_message = fortuneMessage;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        signal: AbortSignal.timeout(8000),
      });
    } catch (e) {
      console.error('Could not set fortune game completion attribute:', e);
    }
  }

  static async removeFortuneGameItems(cart) {
    const fortuneItems = cart.items.filter(item => item.properties?._fortune_game === 'true');
    for (const item of fortuneItems) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
    }
    if (fortuneItems.length > 0 || cart.attributes?._fortune_game_complete) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _fortune_game_complete: '' } }),
      });
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
    // Remove the prize section from the DOM so a fresh scratch card appears
    // when the code is re-applied. Without this, _injectPrizeSectionIfReady
    // sees the existing element and returns early, leaving the old banner in place.
    document.getElementById('fortune-prize-section')?.remove();

    // Reset all in-memory game state for a clean slate.
    this._revealed = false;
    this._wonPrizeVariant = null;
    this._prizeAccepted = false;
    this._fortuneMessage = null;
    this._gameCompleted = false;
  }
}

window.FortuneGameHandler = FortuneGameHandler;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FortuneGameHandler.init());
} else {
  FortuneGameHandler.init();
}
